import { formatDate } from "@lib/intl"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import type { CutNoun, PeriodType } from "@/constants/academic"

/**
 * "Parcial 2", "Trimestre 1": one key per noun (PRD-61 contract), never a
 * noun and a number glued together, so a language that puts the number
 * first can.
 */
export function useCutLabel() {
  const { t } = useTranslation("common")
  return useCallback((noun: CutNoun, number: number) => t(`calendar.cut.${noun}`, { number }), [t])
}

/** "Parcial 1 · 25 ago – 26 sep"; a bound not typed yet reads "sin fecha". */
export function useCutRange() {
  const { t } = useTranslation("common")
  const cutLabel = useCutLabel()
  return useCallback(
    (noun: CutNoun, number: number, start: string | null, end: string | null) =>
      t("calendar.preview.range", {
        cut: cutLabel(noun, number),
        start: start ? formatDate(start, "d MMM") : t("calendar.preview.no_date"),
        end: end ? formatDate(end, "d MMM") : t("calendar.preview.no_date"),
      }),
    [t, cutLabel],
  )
}

/**
 * What one block of the calendar is called, in the institution's own
 * period noun: "Semestre 2", "Cuatrimestre 1", "Año escolar". Never a bare
 * "Periodo 2" while the academic form right above counts "periodos de todo el
 * programa" — a director doing this once a year would read that as "enter 6".
 * "Periodo N" is only the fallback when the type is not known yet.
 */
export function useBlockNames(periodType: PeriodType | undefined, total: number) {
  const { t } = useTranslation("common")
  const name = useCallback(
    (index: number) => {
      const number = index + 1
      // A year split in two is not two "Año escolar".
      if (!periodType || (periodType === "anual" && total > 1)) {
        return t("calendar.period.heading", { number })
      }
      return t(`calendar.block.${periodType}`, { number })
    },
    [t, periodType, total],
  )
  // "fin del semestre": where the last cut closes.
  const end =
    periodType && !(periodType === "anual" && total > 1)
      ? t(`calendar.block_end.${periodType}`)
      : t("calendar.block_end.generic")
  const add = periodType ? t(`calendar.add_block.${periodType}`) : t("calendar.add")
  return { name, end, add }
}
